
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Mail, MailX, RefreshCw, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface EmailLog {
  id: string;
  product_id: string | null;
  recipient_email: string;
  subject: string;
  message: string;
  sent_at: string;
  status: string;
}

export default function EmailLogs() {
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEmailLogs = async () => {
    setLoading(true);
    try {
      console.log('Fetching email logs...');
      
      const { data, error } = await supabase
        .from('email_logs')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(100);

      if (error) {
        console.error('Error fetching email logs:', error);
        throw error;
      }
      
      console.log('Email logs fetched:', data);
      setLogs(data || []);
    } catch (error: any) {
      console.error('Erreur lors du chargement des logs d\'emails:', error);
      toast.error(`Erreur lors du chargement des logs d'emails: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailLogs();
    
    // Set up real-time subscription for email_logs table updates
    const channel = supabase
      .channel('email-logs-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'email_logs' }, 
        (payload) => {
          console.log('Real-time email log update:', payload);
          fetchEmailLogs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch (e) {
      return 'Date invalide';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return <Badge variant="default" className="bg-green-100 text-green-800">Envoyé</Badge>;
      case 'failed':
        return <Badge variant="destructive">Échec</Badge>;
      case 'event_db_triggered':
        return <Badge variant="secondary">Événement DB</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    if (status === 'sent' || status === 'event_db_triggered') {
      return <Mail className="h-4 w-4 text-green-500" />;
    } else {
      return <MailX className="h-4 w-4 text-red-500" />;
    }
  };

  const truncateMessage = (message: string, maxLength: number = 50) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-xl font-bold">
          Historique des emails envoyés 
          {logs.length > 0 && (
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({logs.length} emails)
            </span>
          )}
        </CardTitle>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchEmailLogs} 
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <Table>
            <TableCaption>
              Historique des {logs.length} derniers emails envoyés
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">Date d'envoi</TableHead>
                <TableHead>Destinataire</TableHead>
                <TableHead>Sujet</TableHead>
                <TableHead className="w-[200px]">Message</TableHead>
                <TableHead className="w-[100px]">Statut</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    Aucun email envoyé pour le moment
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-medium">
                      {formatDate(log.sent_at)}
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[200px] truncate" title={log.recipient_email}>
                        {log.recipient_email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[250px] truncate" title={log.subject}>
                        {log.subject}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[200px] truncate" title={log.message}>
                        {truncateMessage(log.message)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(log.status)}
                        {getStatusBadge(log.status)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Détails de l'email</DialogTitle>
                            <DialogDescription>
                              Envoyé le {formatDate(log.sent_at)}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <label className="text-sm font-medium">Destinataire:</label>
                              <p className="text-sm">{log.recipient_email}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium">Sujet:</label>
                              <p className="text-sm">{log.subject}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium">Message:</label>
                              <div className="text-sm bg-muted p-3 rounded-md whitespace-pre-wrap">
                                {log.message}
                              </div>
                            </div>
                            <div>
                              <label className="text-sm font-medium">Statut:</label>
                              <div className="mt-1">
                                {getStatusBadge(log.status)}
                              </div>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
